/** Back office → Agenda (C5): week and day calendar (design v2.1, like Apple Calendar) or the list of
 * upcoming appointments, booking a free slot, closed days (N14). */

import {
  type Appointment,
  type AppointmentModeEnum,
  type AppointmentStatusEnum,
  useAgencyProfileCurrentRetrieve,
  useAppointmentsHolidaysCreate,
  useAppointmentsHolidaysDestroy,
  useAppointmentsHolidaysList,
  useAppointmentsItemsBookCreate,
  useAppointmentsItemsCancelCreate,
  useAppointmentsItemsList,
  useAppointmentsItemsPartialUpdate,
  useAppointmentsTypesList,
  useAppointmentsTypesSlotsList,
  useCrmContactsList,
} from "@agencyos/api-client";
import { formatPhone } from "@agencyos/core";
import {
  Badge,
  Button,
  EmptyState,
  GlassCard,
  GlassSheet,
  IconButton,
  SegmentedControl,
  SelectField,
  Skeleton,
  SoftToggle,
  TextField,
} from "@agencyos/ui-web";
import { CalendarDays, ChevronLeft, ChevronRight, CircleAlert } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Page } from "../components/AppShell";
import { localized } from "../components/TranslatedFields";
import { fieldErrors, useCanEditSettings } from "../lib/settings";
import { Calendar, TYPE_TONES, addDays, dayKey, mondayOf } from "./agenda/Calendar";
import "./agenda/agenda.css";

const STATUSES: AppointmentStatusEnum[] = ["booked", "confirmed", "done", "no_show", "cancelled"];

function useRefreshAgenda() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/appointments/"),
    });
}

function useFormat() {
  const { i18n } = useTranslation();
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return {
    day: (iso: string) =>
      new Date(iso).toLocaleDateString(locale, { timeZone: "Africa/Douala", weekday: "long", day: "numeric", month: "long" }),
    time: (iso: string) => new Date(iso).toLocaleTimeString(locale, { timeZone: "Africa/Douala", hour: "2-digit", minute: "2-digit" }),
    slot: (iso: string) =>
      new Date(iso).toLocaleString(locale, {
        timeZone: "Africa/Douala",
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }),
  };
}

function phoneText(phone?: string | null): string {
  if (!phone) return "";
  try {
    return formatPhone(phone);
  } catch {
    return phone;
  }
}

function BookingForm() {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const refresh = useRefreshAgenda();
  const types = useAppointmentsTypesList();
  const [search, setSearch] = useState("");
  const contacts = useCrmContactsList(search.trim().length >= 2 ? { q: search.trim() } : undefined);
  const [contact, setContact] = useState("");
  const [typeId, setTypeId] = useState("");
  const typeList = types.data?.status === 200 ? types.data.data.results.filter((x) => x.is_active !== false) : [];
  const kind = typeList.find((x) => x.id === typeId) ?? typeList[0];
  const slots = useAppointmentsTypesSlotsList(kind?.id ?? "", { days: 14 }, { query: { enabled: Boolean(kind) } });
  const [slot, setSlot] = useState("");
  const [mode, setMode] = useState<AppointmentModeEnum>("office");
  const [notify, setNotify] = useState(true);
  const book = useAppointmentsItemsBookCreate({ mutation: { onSuccess: refresh } });
  const errors = fieldErrors(book.error);
  const slotList = slots.data?.status === 200 ? slots.data.data : [];
  const modes: AppointmentModeEnum[] = kind?.mode === "office" ? ["office"] : kind?.mode === "call" ? ["call"] : ["office", "call"];
  const contactOptions = contacts.data?.status === 200 ? contacts.data.data.results : [];
  const chosenMode: AppointmentModeEnum = modes.includes(mode) ? mode : (modes[0] ?? "office");

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const starts = slot || slotList[0]?.starts_at;
    if (!kind || !contact || !starts) return;
    book.mutate(
      { data: { contact, type: kind.id, starts_at: starts, mode: chosenMode, notify } },
      { onSuccess: () => setSlot("") },
    );
  };

  return (
    <GlassCard as="section" className="stack">
      <h2>{t("agenda.book")}</h2>
      <form className="stack" onSubmit={onSubmit} noValidate>
        <TextField label={t("agenda.contact")} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        <SelectField
          label={t("agenda.pickContact")}
          value={contact}
          options={[
            { value: "", label: "—" },
            ...contactOptions.map((c) => ({ value: c.id, label: `${c.full_name || "?"} · ${phoneText(c.phone_e164)}` })),
          ]}
          onChange={(e) => setContact(e.target.value)}
        />
        <SelectField
          label={t("agenda.type")}
          value={kind?.id ?? ""}
          options={typeList.map((x) => ({ value: x.id, label: localized(x.name, i18n.language) }))}
          onChange={(e) => {
            setTypeId(e.target.value);
            setSlot("");
          }}
        />
        <SelectField
          label={t("agenda.slot")}
          value={slot || slotList[0]?.starts_at || ""}
          error={errors.starts_at}
          options={
            slotList.length
              ? slotList.map((s) => ({ value: s.starts_at, label: format.slot(s.starts_at) }))
              : [{ value: "", label: t("agenda.noSlot") }]
          }
          onChange={(e) => setSlot(e.target.value)}
        />
        <SelectField
          label={t("agenda.where")}
          value={chosenMode}
          options={modes.map((m) => ({ value: m, label: t(`agenda.mode.${m}`) }))}
          onChange={(e) => setMode(e.target.value as AppointmentModeEnum)}
        />
        <SoftToggle label={t("agenda.notify")} checked={notify} onChange={setNotify} />
        <div className="row">
          <Button type="submit" disabled={book.isPending || !contact || !slotList.length}>
            {t("agenda.confirm")}
          </Button>
          {book.isSuccess ? (
            <span role="status" className="ok">
              {t("agenda.booked")}
            </span>
          ) : null}
        </div>
      </form>
    </GlassCard>
  );
}

function Holidays() {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshAgenda();
  const canEdit = useCanEditSettings();
  const holidays = useAppointmentsHolidaysList();
  const add = useAppointmentsHolidaysCreate({ mutation: { onSuccess: refresh } });
  const remove = useAppointmentsHolidaysDestroy({ mutation: { onSuccess: refresh } });
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("");
  const errors = fieldErrors(add.error);
  const today = new Date().toISOString().slice(0, 10);
  const items = holidays.data?.status === 200 ? holidays.data.data.results.filter((h) => h.date >= today) : [];
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  return (
    <GlassCard as="section" className="stack">
      <h2>{t("agenda.holidays")}</h2>
      <p className="muted">{t("agenda.holidaysHint")}</p>
      <ul className="plain-list">
        {items.map((h) => (
          <li key={h.id} className="row between">
            <span>
              <strong>{new Date(`${h.date}T12:00:00`).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" })}</strong> · {h.label}
            </span>
            {canEdit ? (
              <Button variant="ghost" onClick={() => remove.mutate({ id: h.id })}>
                {t("agenda.remove")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {canEdit ? (
        <form
          className="grid-2"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate({ data: { date, label } }, { onSuccess: () => { setDate(""); setLabel(""); } });
          }}
        >
          <TextField label={t("agenda.holidayDate")} type="date" value={date} error={errors.date} onChange={(e) => setDate(e.target.value)} />
          <TextField label={t("agenda.holidayLabel")} value={label} error={errors.label} onChange={(e) => setLabel(e.target.value)} />
          <div className="row">
            <Button type="submit" variant="secondary" disabled={!date || !label}>
              {t("agenda.addHoliday")}
            </Button>
          </div>
        </form>
      ) : null}
    </GlassCard>
  );
}

type View = "week" | "day" | "list";

/** Phones open on the day view; wider screens on the week. */
function initialView(): View {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 700px)").matches
    ? "day"
    : "week";
}

function AppointmentSheet({ appointment, onClose }: { appointment: Appointment | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const refresh = useRefreshAgenda();
  const update = useAppointmentsItemsPartialUpdate({ mutation: { onSuccess: refresh } });
  const cancel = useAppointmentsItemsCancelCreate({ mutation: { onSuccess: refresh } });
  if (!appointment) return null;
  const a = appointment;
  return (
    <GlassSheet open title={a.contact_name || phoneText(a.contact_phone)} onClose={onClose}>
      <div className="stack">
        <dl className="agenda-facts">
          <div>
            <dt>{t("agenda.when")}</dt>
            <dd>
              {format.day(a.starts_at)} · <span className="ao-num">{format.time(a.starts_at)} – {format.time(a.ends_at)}</span>
            </dd>
          </div>
          <div>
            <dt>{t("agenda.type")}</dt>
            <dd>{localized(a.type_name, i18n.language)}</dd>
          </div>
          <div>
            <dt>{t("agenda.where")}</dt>
            <dd className="row">
              <Badge>{t(`agenda.mode.${a.mode}`)}</Badge>
              {a.booked_by_ai ? <Badge tone="offer">{t("agenda.byAi")}</Badge> : null}
            </dd>
          </div>
          {a.contact_phone ? (
            <div>
              <dt>{t("agenda.phone")}</dt>
              <dd className="ao-num">{phoneText(a.contact_phone)}</dd>
            </div>
          ) : null}
        </dl>
        <SelectField
          label={t("agenda.statusLabel")}
          value={a.status ?? "booked"}
          options={STATUSES.map((s) => ({ value: s, label: t(`agenda.status.${s}`) }))}
          onChange={(e) =>
            e.target.value === "cancelled"
              ? cancel.mutate({ id: a.id }, { onSuccess: onClose })
              : update.mutate({ id: a.id, data: { status: e.target.value as AppointmentStatusEnum } }, { onSuccess: onClose })
          }
        />
      </div>
      <div className="ao-sheet__actions">
        <Button variant="secondary" onClick={onClose}>
          {t("common.close")}
        </Button>
      </div>
    </GlassSheet>
  );
}

function UpcomingList({ items, onOpen }: { items: readonly Appointment[]; onOpen: (a: Appointment) => void }) {
  const { t, i18n } = useTranslation();
  const format = useFormat();
  const refresh = useRefreshAgenda();
  const update = useAppointmentsItemsPartialUpdate({ mutation: { onSuccess: refresh } });
  const cancel = useAppointmentsItemsCancelCreate({ mutation: { onSuccess: refresh } });
  const byDay = new Map<string, Appointment[]>();
  for (const item of items) {
    const key = format.day(item.starts_at);
    byDay.set(key, [...(byDay.get(key) ?? []), item]);
  }
  return (
    <div className="stack agenda-list">
      {[...byDay.entries()].map(([day, list]) => (
        <section key={day} className="stack" aria-label={day}>
          <h3 className="agenda-day">{day}</h3>
          <ul className="plain-list agenda-rows">
            {list.map((a) => (
              <li key={a.id} className="agenda-row">
                <time className="timeline__time ao-num" dateTime={a.starts_at}>
                  {format.time(a.starts_at)}
                </time>
                <button type="button" className="agenda-row__who" onClick={() => onOpen(a)}>
                  <strong>{a.contact_name || phoneText(a.contact_phone)}</strong>
                  <span className="muted">{localized(a.type_name, i18n.language)}</span>
                </button>
                <span className="row">
                  <Badge>{t(`agenda.mode.${a.mode}`)}</Badge>
                  {a.booked_by_ai ? <Badge tone="offer">{t("agenda.byAi")}</Badge> : null}
                </span>
                <SelectField
                  label={t("agenda.statusLabel")}
                  value={a.status ?? "booked"}
                  options={STATUSES.map((s) => ({ value: s, label: t(`agenda.status.${s}`) }))}
                  onChange={(e) =>
                    e.target.value === "cancelled"
                      ? cancel.mutate({ id: a.id })
                      : update.mutate({ id: a.id, data: { status: e.target.value as AppointmentStatusEnum } })
                  }
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function AgendaPage() {
  const { t, i18n } = useTranslation();
  const profile = useAgencyProfileCurrentRetrieve();
  const types = useAppointmentsTypesList();
  const [view, setView] = useState<View>(initialView);
  const [anchor, setAnchor] = useState(() => dayKey(new Date()));
  const [opened, setOpened] = useState<Appointment | null>(null);
  const start = view === "week" ? mondayOf(anchor) : anchor;
  const span = view === "week" ? 7 : view === "day" ? 1 : 14;
  const appointments = useAppointmentsItemsList({ start, days: span });
  const hours = profile.data?.status === 200 ? (profile.data.data.opening_hours as Record<string, unknown> | undefined) : undefined;
  const items = appointments.data?.status === 200 ? appointments.data.data.results : [];
  const typeList = types.data?.status === 200 ? types.data.data.results : [];
  const typeOrder = typeList.map((x) => x.id);
  const days = Array.from({ length: view === "week" ? 7 : 1 }, (_, i) => addDays(start, i));
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const label = (day: string, options: Intl.DateTimeFormatOptions) =>
    new Date(`${day}T12:00:00Z`).toLocaleDateString(locale, { ...options, timeZone: "UTC" });
  const title =
    view === "week"
      ? `${label(start, { day: "numeric", month: "short" })} – ${label(addDays(start, 6), { day: "numeric", month: "short", year: "numeric" })}`
      : view === "day"
        ? label(start, { weekday: "long", day: "numeric", month: "long", year: "numeric" })
        : t("agenda.upcoming");
  const step = view === "week" ? 7 : view === "day" ? 1 : 14;
  const shown = view === "list" ? items : items.filter((a) => a.status !== "cancelled");
  const legend = typeList.filter((x) => items.some((a) => a.type === x.id));
  const toneOf = (id: string) => TYPE_TONES[Math.max(0, typeOrder.indexOf(id)) % TYPE_TONES.length] ?? "accent";

  return (
    <Page title={t("agenda.title")} description={t("agenda.intro")}>
      {profile.data?.status === 200 && (!hours || Object.keys(hours).length === 0) ? (
        <p role="alert" className="notice-warning ao-glass ao-card agenda-notice">
          <CircleAlert size={16} strokeWidth={1.75} aria-hidden="true" /> {t("agenda.noHours")}
        </p>
      ) : null}
      <GlassCard as="section" className="stack agenda-card" aria-label={t("agenda.calendar")}>
        <div className="agenda-toolbar">
          <div className="agenda-toolbar__nav">
            <Button variant="secondary" size="sm" onClick={() => setAnchor(dayKey(new Date()))}>
              {t("agenda.today")}
            </Button>
            <IconButton label={t("agenda.previous")} size="sm" onClick={() => setAnchor(addDays(anchor, -step))}>
              <ChevronLeft size={16} strokeWidth={2} aria-hidden="true" />
            </IconButton>
            <IconButton label={t("agenda.next")} size="sm" onClick={() => setAnchor(addDays(anchor, step))}>
              <ChevronRight size={16} strokeWidth={2} aria-hidden="true" />
            </IconButton>
            <h2 className="agenda-toolbar__title">{title}</h2>
          </div>
          <SegmentedControl<View>
            label={t("agenda.viewLabel")}
            value={view}
            options={[
              { value: "day", label: t("agenda.view.day") },
              { value: "week", label: t("agenda.view.week") },
              { value: "list", label: t("agenda.view.list") },
            ]}
            onChange={setView}
          />
        </div>
        {legend.length ? (
          <ul className="agenda-legend" aria-label={t("agenda.legend")}>
            {legend.map((x) => (
              <li key={x.id} className={`ao-tone--${toneOf(x.id)}`}>
                <span className="agenda-legend__dot" aria-hidden="true" />
                {localized(x.name, i18n.language)}
              </li>
            ))}
          </ul>
        ) : null}
        {appointments.isPending ? <Skeleton style={{ height: 420, borderRadius: 16 }} /> : null}
        {!appointments.isPending && view === "list" && !items.length ? (
          <EmptyState compact icon={<CalendarDays size={22} strokeWidth={1.75} />} title={t("agenda.none")} />
        ) : null}
        {!appointments.isPending && view === "list" ? <UpcomingList items={items} onOpen={setOpened} /> : null}
        {!appointments.isPending && view !== "list" ? (
          <div className="cal-scroll">
            <Calendar days={days} items={shown} typeOrder={typeOrder} openingHours={hours} onOpen={setOpened} />
          </div>
        ) : null}
        {!appointments.isPending && view === "day" && !shown.length ? <p className="muted agenda-empty-day">{t("agenda.noneDay")}</p> : null}
      </GlassCard>
      <div className="bento">
        <div className="bento__half">
          <BookingForm />
        </div>
        <div className="bento__half">
          <Holidays />
        </div>
      </div>
      <AppointmentSheet appointment={opened} onClose={() => setOpened(null)} />
    </Page>
  );
}
